import React, {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useRef,
  FC,
  ReactNode,
} from 'react';
import {
  TProfile,
  ProfileAction,
  ProfileContextType,
  TMyFamily,
  TFamilyUser,
  TUserProfileData,
  OpenInvitationProps,
} from '../@types/user';
import { UserContext } from './userContext';

const initialFamilyState: TFamilyUser = {
  messagingActive: true,
  hideBilling: false,
  familyMessaging: false,
  guardianAccount: false,
  isMinor: false,
  guardianVerified: false,
  connectionApproval: false,
  guardianSchedule: false,
  userCanUpdate: true,
  delegateCommunicationsSettings: false,
  communityActive: true,
  communityPostActive: true,
};

const iState: TProfile = {
  activeView: 'main',
  firstName: '',
  lastName: '',
  username: '',
  email: '',
  isStaff: false,
  isHost: false,
  verified: false,
  archived: false,
  avatar: '',
  uict: '',
  uuid: '',
  nickname: '',
  family: undefined,
  familyUser: undefined,
  familyMembers: undefined,
  invitations: undefined,
  messagingActive: true,
  hideBilling: false,
  familyMessaging: false,
  guardianAccount: false,
  isMinor: false,
  guardianVerified: false,
  connectionApproval: false,
  guardianSchedule: false,
  userCanUpdate: true,
  delegateCommunicationsSettings: false,
  communityActive: true,
  communityPostActive: true,
  stripeConnectAccountId: null,
  stripeConnectVerified: null,
};
export const ProfileContext = createContext<ProfileContextType>({
  profileState: iState,
  dispatch: () => null,
  setActiveView: () => null,
  setProfileData: () => null,
  setMyFamily: () => null,
  setFamilyPermissions: () => null,
  setAvatar: () => null,
  clearProfile: () => null,
  setInvitations: () => null,
  setMessagingActive: () => null,
  logOut: () => Promise.resolve(),
});

export const profileReducer = (
  profileState: TProfile,
  action: ProfileAction,
): TProfile => {
  switch (action.type) {
    case 'SET_FAMILY_PERMISSIONS':
      return {
        ...profileState,
        messagingActive: action.payload.messagingActive,
        hideBilling: action.payload.hideBilling,
        familyMessaging: action.payload.familyMessaging,
        guardianAccount: action.payload.guardianAccount,
        isMinor: action.payload.isMinor,
        guardianVerified: action.payload.guardianVerified,
        connectionApproval: action.payload.connectionApproval,
        guardianSchedule: action.payload.guardianSchedule,
        userCanUpdate: action.payload.userCanUpdate,
        communityActive: action.payload.communityActive,
        communityPostActive: action.payload.communityPostActive,
      };
    case 'SET_PROFILE_DATA':
      return {
        ...profileState,
        activeView: action.payload.activeView || profileState.activeView,
        uict: action.payload.uict,
        firstName: action.payload.firstName,
        lastName: action.payload.lastName,
        username: action.payload.username,
        email: action.payload.email,
        isStaff: action.payload.isStaff,
        isHost: action.payload.isHost,
        verified: action.payload.verified,
        archived: action.payload.archived || false, // default to false if not provided
        nickname: action.payload.nickname,
        avatar: action.payload.avatar,
        uuid: action.payload.uict,
        family: action.payload.family,
        stripeConnectAccountId: action.payload.stripeConnectAccountId ?? null,
        stripeConnectVerified: action.payload.stripeConnectVerified ?? null,
      };
    case 'SET_MESSAGING_ACTIVE':
      return {
        ...profileState,
        messagingActive: action.payload,
      };
    case 'SET_NEW_FAMILY_DATA':
      return {
        ...profileState,
        family: action.payload.family,
        familyMembers: action.payload.familyMembers,
      };
    case 'SET_FAMILY_DATA':
      return {
        ...profileState,
        family: action.payload.family,
        familyMembers: action.payload.family.familyMembers,
      };
    case 'SET_ACTIVE_VIEW':
      return {
        ...profileState,
        activeView: action.payload,
      };
    case 'SET_AVATAR':
      return {
        ...profileState,
        avatar: action.payload,
      };
    case 'SET_INVITATIONS':
      return {
        ...profileState,
        invitations: action.payload,
      };
    case 'CLEAR_PROFILE':
      return {
        ...iState,
      };

    default:
      return profileState;
  }
};

const ProfileProvider: FC<{
  children: ReactNode;
  onSetActiveView?: (payload: TProfile['activeView']) => void;
  onSetProfileData?: (props: TUserProfileData) => void;
  onSetMyFamily?: (payload: TMyFamily) => void;
  onSetFamilyPermissions?: (payload: TFamilyUser) => void;
  onSetInvitations?: (payload: OpenInvitationProps[]) => void;
  onSetAvatar?: (payload: TProfile['avatar']) => void;
  onSetMessagingActive?: (payload: boolean) => void;
}> = ({
  children,
  onSetActiveView,
  onSetProfileData,
  onSetMyFamily,
  onSetFamilyPermissions,
  onSetInvitations,
  onSetAvatar,
  onSetMessagingActive,
}) => {
  const isBrowser = () => typeof window !== 'undefined';
  const storedState = isBrowser() && localStorage.getItem('cs-fx');
  const { signOut, setMessage, state: userState } = useContext(UserContext);

  const getEnv = (env: string | undefined) => {
    const val = env && env;
    return val;
  };

  /**
   * This key-value js object holds codes that are used to set family permissions.
   * Codes are set in the .env file and used to determine
   * which permissions are active for the user. Codes are stored in localStorage
   * as a string and decoded to set the initial state for family permissions.
   */
  const cookieCodes = {
    messagingActive: getEnv(process.env.GATSBY_MESSAGING_ACTIVE),
    hideBilling: getEnv(process.env.GATSBY_HIDE_BILLING),
    familyMessaging: getEnv(process.env.GATSBY_FAMILY_MESSAGING),
    guardianAccount: getEnv(process.env.GATSBY_GUARDIAN_ACCOUNT),
    isMinor: getEnv(process.env.GATSBY_IS_MINOR),
    connectionApproval: getEnv(process.env.GATSBY_CONNECTION_APPROVAL),
    guardianSchedule: getEnv(process.env.GATSBY_GUARDIAN_SCHEDULE),
    userCanUpdate: getEnv(process.env.GATSBY_USER_CAN_UPDATE),
    delegateCommunicationsSettings: getEnv(
      process.env.GATSBY_DELEGATE_COMMUNICATIONS_SETTINGS,
    ),
    communityActive: getEnv(process.env.GATSBY_COMMUNITY_ACTIVE),
    communityPostActive: getEnv(process.env.GATSBY_COMMUNITY_POST_ACTIVE),
  };

  /**
   *
   * @param storedCodes - a string of codes from localStorage
   * This function decodes the stored cookie codes from localStorage
   * @returns
   * It returns an object with boolean values for each family permission.
   * If the code is present in the stored string, the permission is true.
   */
  const getStateDecoder: (storedCodes: string) => TFamilyUser = storedCodes => {
    const ps: string = JSON.parse(storedCodes);
    const decodedState: any = {};
    for (const [key, value] of Object.entries(cookieCodes)) {
      let keyName = key;
      if (value && ps.includes(value)) {
        decodedState[keyName] = true;
      } else {
        decodedState[keyName] = false;
      }
    }
    return decodedState;
  };

  /**
   * This is the initial state for family parameters in the profile context.
   * It decodes the stored state from localStorage if it exists.
   * If it does not exist, it uses the initialFamilyState.
   */
  const familyState: TFamilyUser = storedState
    ? getStateDecoder(storedState)
    : initialFamilyState;

  /**
   * The initial state for the profile context.
   * It combines the initialState with the familyState.
   */
  const initState: TProfile = {
    ...iState,
    ...familyState,
  };
  const localState: TProfile = initState;

  const [profileState, dispatch] = useReducer(profileReducer, localState);

  /**
   *
   * @param data - TFamilyUser
   * This function takes the family user state and returns a string of cookie codes
   * @returns
   * This string is used to set the localStorage for family permissions.
   * It concatenates the codes for each permission that is true.
   */
  const getCookieCodes = (data: TFamilyUser) => {
    let codes: string = '';
    for (const [key, value] of Object.entries(data)) {
      if (value === true) {
        const stateKey = key;
        for (const [key, value] of Object.entries(cookieCodes)) {
          if (key === stateKey) {
            codes = codes + value;
          }
        }
      }
    }
    return codes;
  };
  useEffect(() => {
    handleCodeLocalStorage(profileState);
  }, [profileState]);

  const handleCodeLocalStorage = (props: TFamilyUser) => {
    const familyPerms: TFamilyUser = {
      messagingActive: props.messagingActive,
      hideBilling: props.hideBilling,
      familyMessaging: props.familyMessaging,
      guardianAccount: props.guardianAccount,
      isMinor: props.isMinor,
      guardianVerified: props.guardianVerified,
      connectionApproval: props.connectionApproval,
      guardianSchedule: props.guardianSchedule,
      userCanUpdate: props.userCanUpdate,
      delegateCommunicationsSettings: props.delegateCommunicationsSettings,
      communityActive: props.communityActive,
      communityPostActive: props.communityPostActive,
    };
    const codedState = getCookieCodes(familyPerms);
    isBrowser() && localStorage.setItem('cs-fx', JSON.stringify(codedState));
  };

  const setActiveView = (activeView: TProfile['activeView']) => {
    dispatch({ type: 'SET_ACTIVE_VIEW', payload: activeView });
    onSetActiveView && onSetActiveView(activeView);
  };
  const setProfileData = (props: TUserProfileData) => {
    dispatch({ type: 'SET_PROFILE_DATA', payload: props });
    onSetProfileData && onSetProfileData(props);
  };
  const setMessagingActive = (payload: boolean) => {
    dispatch({ type: 'SET_MESSAGING_ACTIVE', payload });
    onSetMessagingActive && onSetMessagingActive(payload);
  };

  const setMyFamily = (payload: TMyFamily) => {
    if (!payload.familyMembers && payload.family.familyMembers) {
      dispatch({ type: 'SET_FAMILY_DATA', payload });
    } else {
      dispatch({ type: 'SET_NEW_FAMILY_DATA', payload });
    }
    onSetMyFamily && onSetMyFamily(payload);
  };
  const setFamilyPermissions = (props: TFamilyUser) => {
    handleCodeLocalStorage(props);
    dispatch({ type: 'SET_FAMILY_PERMISSIONS', payload: props });
    onSetFamilyPermissions && onSetFamilyPermissions(props);
  };
  const setAvatar = (payload: TProfile['avatar']) => {
    dispatch({ type: 'SET_AVATAR', payload });
    onSetAvatar && onSetAvatar(payload);
  };
  const clearProfile = () => {
    handleCodeLocalStorage(initialFamilyState);
    dispatch({ type: 'CLEAR_PROFILE' });
  };

  // profileState (name, avatar, email, family, Stripe Connect fields) is only
  // repopulated lazily by whichever page happens to mount and refetch it
  // (CheckoutContact, ManageProfile, UserProfileNav). Every other consumer
  // just reads whatever's already in context, so without this, logging in as
  // a new user in the same tab leaves the previous user's identity data
  // rendered app-wide until one of those three pages happens to load.
  // Track transitions (not just isLoggedIn truthiness) so a page load that
  // starts already-logged-in doesn't clobber the family-permission overlay
  // decoded from localStorage at init.
  const prevIsLoggedIn = useRef(userState.isLoggedIn);
  useEffect(() => {
    if (!prevIsLoggedIn.current && userState.isLoggedIn) {
      clearProfile();
    }
    prevIsLoggedIn.current = userState.isLoggedIn;
  }, [userState.isLoggedIn]);
  const setInvitations = (payload: OpenInvitationProps[]) => {
    dispatch({ type: 'SET_INVITATIONS', payload });
    onSetInvitations && onSetInvitations(payload);
  };
  const logOut = async () => {
    setMessage('Signing you out...');
    clearProfile();
    await signOut();
  };
  return (
    <ProfileContext.Provider
      value={{
        profileState,
        setActiveView,
        setProfileData,
        setMyFamily,
        setFamilyPermissions,
        setAvatar,
        clearProfile,
        setInvitations,
        setMessagingActive,
        logOut,
        dispatch,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
};

export default ProfileProvider;
